import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Plus, 
  Users,
  Trash2,
  MapPin,
  Loader2,
  Shield,
  User as UserIcon,
  Edit,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const UsersPage = () => {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'account_user',
    pin: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    pin: ''
  });
  const [selectedLocations, setSelectedLocations] = useState([]);

  const fetchData = async () => {
    try {
      const [usersRes, locationsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/locations`)
      ]);
      setUsers(usersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API}/users`, formData);

      // If a PIN was entered, set it after user creation
      if (formData.pin && res.data?.id) {
        await axios.put(`${API}/users/${res.data.id}/pin`, { current_pin: '', new_pin: formData.pin });
      }

      toast.success('User created successfully');
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'account_user', pin: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      pin: ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    
    try {
      const updateData = {};
      if (editFormData.name !== selectedUser.name) updateData.name = editFormData.name;
      if (editFormData.email !== selectedUser.email) updateData.email = editFormData.email;
      if (editFormData.password) updateData.password = editFormData.password;
      if (editFormData.role !== selectedUser.role) updateData.role = editFormData.role;

      const pinToSet = editFormData.pin ? editFormData.pin : null;
      
      if (Object.keys(updateData).length === 0 && !pinToSet) {
        toast.info('No changes to save');
        setEditDialogOpen(false);
        return;
      }
      
      if (Object.keys(updateData).length > 0) {
        await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      }
      if (pinToSet) {
        await axios.put(`${API}/users/${selectedUser.id}/pin`, { current_pin: '', new_pin: pinToSet });
      }

      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setSubmitting(true);
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('Removal request submitted');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to request user removal:', error);
      toast.error(error.response?.data?.detail || 'Failed to request user removal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHardDeleteUser = async () => {
    if (!selectedUser) return;

    setSubmitting(true);
    try {
      await axios.delete(`${API}/users/${selectedUser.id}/hard`);
      toast.success('User permanently deleted');
      setHardDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const openLocationDialog = (user) => {
    setSelectedUser(user);
    setSelectedLocations(user.location_ids || []);
    setLocationDialogOpen(true);
  };

  const handleUpdateLocations = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    
    try {
      await axios.put(`${API}/users/${selectedUser.id}/locations`, {
        location_ids: selectedLocations
      });
      toast.success('User locations updated');
      setLocationDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update locations');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLocation = (locationId) => {
    if (selectedLocations.includes(locationId)) {
      setSelectedLocations(selectedLocations.filter(id => id !== locationId));
    } else {
      setSelectedLocations([...selectedLocations, locationId]);
    }
  };

  const getLocationNames = (locationIds) => {
    if (!locationIds || locationIds.length === 0) return 'No locations';
    return locationIds.map(id => {
      const loc = locations.find(l => l.id === id);
      return loc ? loc.name : 'Unknown';
    }).join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage platform users and their access</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user to the platform
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="account_user">Account User — location-scoped</SelectItem>
                      <SelectItem value="account_admin">Account Admin — location-scoped admin</SelectItem>
                      <SelectItem value="agency_support">Agency Support — cross-location support</SelectItem>
                      <SelectItem value="agency_admin">Agency Admin — full access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pin">PIN (4 digits, optional)</Label>
                  <Input
                    id="pin"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="••••"
                    value={formData.pin}
                    onChange={(e) => {
                      const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                      setFormData({ ...formData, pin: v });
                    }}
                    data-testid="user-pin-input"
                  />
                </div>

              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  disabled={submitting}
                  data-testid="create-user-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Users className="w-5 h-5 text-indigo-600" />
            Platform Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Locations</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No users found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          user.role === 'admin' ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {user.role === 'admin' ? (
                            <Shield className="w-4 h-4" />
                          ) : (
                            <UserIcon className="w-4 h-4" />
                          )}
                        </div>
                        <span className="font-medium text-slate-800">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell>
                      {(() => {
                        const cfg = {
                          agency_admin:   { label: 'Agency Admin',   cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
                          agency_support: { label: 'Agency Support', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
                          account_admin:  { label: 'Account Admin',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
                          account_user:   { label: 'Account User',   cls: 'bg-slate-100 text-slate-700 border-slate-200' },
                          // legacy fallbacks
                          super_admin:   { label: 'Super Admin (legacy)',   cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
                          admin:         { label: 'Admin (legacy)',         cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
                          support_agent: { label: 'Support Agent (legacy)', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
                          user:          { label: 'User (legacy)',          cls: 'bg-slate-100 text-slate-700 border-slate-200' },
                        };
                        const c = cfg[user.role] || { label: user.role || 'Unknown', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
                        return (
                          <Badge variant="outline" className={`${c.cls} text-xs`} data-testid={`user-role-badge-${user.id}`}>
                            {c.label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => openLocationDialog(user)}
                        className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline flex items-center gap-1"
                        data-testid={`manage-locations-${user.id}`}
                      >
                        <MapPin className="w-3 h-3" />
                        {user.location_ids?.length || 0} locations
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedUser(user); setDeleteDialogOpen(true); }}
                          className="text-slate-500 hover:text-amber-600 hover:bg-amber-50"
                          title="Request removal"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {isSuperAdmin() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedUser(user); setHardDeleteDialogOpen(true); }}
                            className="text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                            title="Permanently delete user (Super Admin)"
                            data-testid={`hard-delete-user-${user.id}`}
                          >
                            <Shield className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit User</DialogTitle>
            <DialogDescription>
              Update user profile information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  placeholder="John Smith"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  placeholder="john@company.com"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                  data-testid="edit-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Leave blank to keep current"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    data-testid="edit-user-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Leave blank to keep current password</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select 
                  value={editFormData.role} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="account_user">Account User — location-scoped</SelectItem>
                    <SelectItem value="account_admin">Account Admin — location-scoped admin</SelectItem>
                    <SelectItem value="agency_support">Agency Support — cross-location support</SelectItem>
                    <SelectItem value="agency_admin">Agency Admin — full access</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-pin">Reset PIN (4 digits, optional)</Label>
                <Input
                  id="edit-pin"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="••••"
                  value={editFormData.pin}
                  onChange={(e) => {
                    const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                    setEditFormData({ ...editFormData, pin: v });
                  }}
                  data-testid="edit-user-pin"
                />
                <p className="text-xs text-slate-500">Leave blank to keep the current PIN</p>
              </div>

            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={submitting}
                data-testid="save-user-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Location Assignment Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Manage Locations for {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Select which locations this user can access
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3 max-h-[300px] overflow-y-auto">
            {locations.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No locations available</p>
            ) : (
              locations.map((location) => (
                <div
                  key={location.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedLocations.includes(location.id)
                      ? 'border-indigo-300 bg-indigo-50'
                      : 'border-slate-200 hover:border-indigo-200 hover:bg-slate-50'
                  }`}
                  onClick={() => toggleLocation(location.id)}
                  data-testid={`location-checkbox-${location.id}`}
                >
                  <Checkbox
                    checked={selectedLocations.includes(location.id)}
                    onCheckedChange={() => toggleLocation(location.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{location.name}</p>
                    <p className="text-xs text-slate-500">ID: {location.location_id}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateLocations}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={submitting}
              data-testid="save-locations-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Locations'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request User Removal</AlertDialogTitle>
            <AlertDialogDescription>
              Submit a removal request for <span className="font-medium">{selectedUser?.name}</span>. A super admin will review and approve the removal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-delete-user"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete (Super Admin only) */}
      <AlertDialog open={hardDeleteDialogOpen} onOpenChange={setHardDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This immediately removes <span className="font-medium">{selectedUser?.name}</span> ({selectedUser?.email}) from the platform. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDeleteUser}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-hard-delete-user"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
